/**
 * Workflows API Client
 * Handles AI workflow history and management endpoints
 */

import axios from 'axios';

/**
 * Fetch workflow history with filtering, sorting, and pagination
 *
 * @param {Object} params - Query parameters
 * @param {number} [params.limit=20] - Number of workflows to return
 * @param {number} [params.offset=0] - Number of workflows to skip
 * @param {string} [params.status] - Filter by status (completed, failed, executing, etc.)
 * @param {string} [params.date_from] - Filter workflows created/completed after this date (ISO 8601)
 * @param {string} [params.date_to] - Filter workflows created/completed before this date (ISO 8601)
 * @param {string} [params.date_field='created_at'] - Date field to filter on (created_at or completed_at)
 * @param {string} [params.sort_by='created_at'] - Sort field
 * @param {string} [params.sort_order='desc'] - Sort order (asc or desc)
 * @param {boolean} [params.include_cost_breakdown=true] - Include detailed cost breakdown
 * @param {string} [params.cost_breakdown_detail='summary'] - Cost breakdown detail level (summary or full)
 * @returns {Promise} Promise resolving to workflow history response
 */
export async function fetchWorkflowHistory(params = {}) {
  try {
    // Build query params, only including defined values
    const queryParams = {
      limit: params.limit || 20,
      offset: params.offset || 0,
      date_field: params.date_field || 'created_at',
      sort_by: params.sort_by || 'created_at',
      sort_order: params.sort_order || 'desc',
      include_cost_breakdown: params.include_cost_breakdown !== false,
      cost_breakdown_detail: params.cost_breakdown_detail || 'summary',
      summarize_descriptions: params.summarize_descriptions || false
    };

    // Only add optional filters if they have values
    if (params.status) {
      queryParams.status = params.status;
    }
    if (params.date_from) {
      queryParams.date_from = params.date_from;
    }
    if (params.date_to) {
      queryParams.date_to = params.date_to;
    }

    const response = await axios.get('/api/v1/workflows/history', {
      params: queryParams,
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching workflow history:', error);
    throw error;
  }
}

/**
 * Fetch a single workflow by ID
 *
 * @param {string} workflowId - Workflow UUID
 * @returns {Promise} Promise resolving to workflow details
 */
export async function fetchWorkflowById(workflowId) {
  try {
    const response = await axios.get(`/api/v1/workflows/${workflowId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching workflow ${workflowId}:`, error);
    throw error;
  }
}
